import AddIcon from '@mui/icons-material/Add';
import { Button, Stack, Typography } from '@mui/material';

import {
    TemplateHeaderFormat,
    WhatsAppCarouselCard,
    WhatsAppQuickReplyButton,
    WhatsAppUrlButton,
} from 'domains/content/types';

import CarouselCard from './CarouselCard';

const MAX_CARDS = 10;

const newCard = (): WhatsAppCarouselCard => ({
    header_format: TemplateHeaderFormat.IMAGE,
    header_url: '',
    body_text: '',
    buttons: [],
});

type CarouselButton = WhatsAppQuickReplyButton | WhatsAppUrlButton;

export type Props = {
    cards: WhatsAppCarouselCard[];
    onAddCard: (card: WhatsAppCarouselCard) => void;
    onUpdateCard: (index: number, card: WhatsAppCarouselCard) => void;
    onRemoveCard: (index: number) => void;
};

const WhatsAppCarouselSection = ({ cards, onAddCard, onUpdateCard, onRemoveCard }: Props) => {
    // All cards share the same button configuration — taken from the first card
    const sharedButtonConfig: CarouselButton[] = (cards[0]?.buttons ?? []) as CarouselButton[];

    const handleSharedButtonConfigChange = (buttons: CarouselButton[]) => {
        // Propagate new button config to ALL cards
        cards.forEach((card, i) => {
            onUpdateCard(i, { ...card, buttons });
        });
    };

    const atMax = cards.length >= MAX_CARDS;

    return (
        <Stack spacing={2} data-testid="whatsapp-carousel-section">
            <Typography variant="subtitle2" fontWeight={600}>
                Carousel Cards{' '}
                <Typography component="span" variant="caption" color="text.secondary">
                    (1–{MAX_CARDS} cards · Marketing only)
                </Typography>
            </Typography>

            {cards.map((card, index) => (
                <CarouselCard
                    key={index}
                    card={card}
                    index={index}
                    canRemove={cards.length > 1}
                    sharedButtonConfig={sharedButtonConfig}
                    onCardChange={onUpdateCard}
                    onSharedButtonConfigChange={handleSharedButtonConfigChange}
                    onRemove={onRemoveCard}
                />
            ))}

            <Button
                data-testid="add-carousel-card"
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => onAddCard(newCard())}
                disabled={atMax}
                sx={{ alignSelf: 'flex-start' }}
                aria-label="Add carousel card"
            >
                {atMax ? `Maximum ${MAX_CARDS} cards` : 'Add card'}
            </Button>
        </Stack>
    );
};

export default WhatsAppCarouselSection;
